import { Controller } from "@hotwired/stimulus"
import { readFilterParams, writeFilterParams } from "../utils/url_filters"

const URL_KEYS = ["date_from", "date_to"]

export default class extends Controller {
  static targets = ["allButton", "dateButton", "dateIndicator", "datePickerWrapper", "datePickerCalendar"]

  connect() {
    this.dateFrom = ""
    this.dateTo = ""
    this.boundCloseDatePicker = this.closeDatePickerOutside.bind(this)
    this.restoreFromUrl()
    this.updateAllButtonStyle()

    if (this.hasAnyFilter()) {
      setTimeout(() => this.dispatchFilters(), 0)
    }
  }

  restoreFromUrl() {
    const params = readFilterParams(URL_KEYS)

    if (params.date_from && params.date_to) {
      this.dateFrom = params.date_from
      this.dateTo = params.date_to
      if (this.hasDatePickerCalendarTarget) {
        this.datePickerCalendarTarget.dataset.dateFrom = params.date_from
        this.datePickerCalendarTarget.dataset.dateTo = params.date_to
      }
      this.updateDateIndicator()
    }
  }

  hasAnyFilter() {
    return this.dateFrom && this.dateTo
  }

  disconnect() {
    document.removeEventListener("click", this.boundCloseDatePicker)
  }

  toggleDatePicker(event) {
    event.preventDefault()
    event.stopPropagation()

    if (!this.hasDatePickerCalendarTarget) return

    const isOpen = this.datePickerCalendarTarget.classList.contains("open")

    if (isOpen) {
      this.closeDatePicker()
    } else {
      this.datePickerCalendarTarget.classList.add("open")

      const pickerController = this.application.getControllerForElementAndIdentifier(
        this.datePickerCalendarTarget, "date-range-picker"
      )
      if (pickerController) pickerController.open()

      document.addEventListener("click", this.boundCloseDatePicker)
    }
  }

  applyDateRange(event) {
    event.preventDefault()
    if (!this.hasDatePickerCalendarTarget) return

    const dateFrom = this.datePickerCalendarTarget.dataset.dateFrom
    const dateTo = this.datePickerCalendarTarget.dataset.dateTo
    if (!dateFrom || !dateTo) return

    this.dateFrom = dateFrom
    this.dateTo = dateTo
    this.updateDateIndicator()
    this.updateAllButtonStyle()
    this.closeDatePicker()
    this.dispatchFilters()
  }

  clearDateRange(event) {
    event.preventDefault()

    const pickerController = this.application.getControllerForElementAndIdentifier(
      this.datePickerCalendarTarget, "date-range-picker"
    )
    if (pickerController) pickerController.clear()

    this.dateFrom = ""
    this.dateTo = ""
    this.updateDateIndicator()
    this.updateAllButtonStyle()
    this.closeDatePicker()
    this.dispatchFilters()
  }

  resetFilters(event) {
    event.preventDefault()
    this.dateFrom = ""
    this.dateTo = ""

    if (this.hasDatePickerCalendarTarget) {
      const pickerController = this.application.getControllerForElementAndIdentifier(
        this.datePickerCalendarTarget, "date-range-picker"
      )
      if (pickerController) pickerController.clear()
    }

    this.updateDateIndicator()
    this.updateAllButtonStyle()
    this.closeDatePicker()
    this.dispatchFilters()
  }

  dispatchFilters() {
    this.dispatch("filtersChanged", {
      detail: {
        dateFrom: this.dateFrom,
        dateTo: this.dateTo
      }
    })
    this.updatePdfExportLinks()
    this.updateUrl()
  }

  updateUrl() {
    writeFilterParams({
      date_from: this.dateFrom,
      date_to: this.dateTo
    })
  }

  updatePdfExportLinks() {
    document.querySelectorAll("[data-pdf-export-link]").forEach((link) => {
      const url = new URL(link.href)

      this.setOrDeleteParam(url, "date_from", this.dateFrom)
      this.setOrDeleteParam(url, "date_to", this.dateTo)

      link.href = url.toString()
    })
  }

  setOrDeleteParam(url, key, value) {
    if (value) {
      url.searchParams.set(key, value)
    } else {
      url.searchParams.delete(key)
    }
  }

  closeDatePickerOutside(event) {
    if (!this.hasDatePickerWrapperTarget) return
    if (this.datePickerWrapperTarget.contains(event.target)) return
    this.closeDatePicker()
  }

  closeDatePicker() {
    if (this.hasDatePickerCalendarTarget) {
      this.datePickerCalendarTarget.classList.remove("open")
    }
    document.removeEventListener("click", this.boundCloseDatePicker)
  }

  updateDateIndicator() {
    if (!this.hasDateIndicatorTarget || !this.hasDateButtonTarget) return

    if (this.dateFrom && this.dateTo) {
      this.dateIndicatorTarget.textContent = "●"
      this.dateIndicatorTarget.classList.remove("d-none")
      this.dateButtonTarget.classList.add("is-active")
    } else {
      this.dateIndicatorTarget.classList.add("d-none")
      this.dateButtonTarget.classList.remove("is-active")
    }
  }

  updateAllButtonStyle() {
    const hasAnyFilter = this.dateFrom && this.dateTo

    if (hasAnyFilter) {
      this.allButtonTarget.classList.remove("text-disabled-green")
      this.allButtonTarget.classList.add("text-green-dark-background")
    } else {
      this.allButtonTarget.classList.add("text-disabled-green")
      this.allButtonTarget.classList.remove("text-green-dark-background")
    }
  }
}
